/* eslint-disable complexity */
import classnames from 'classnames';
import { addYears, format } from 'date-fns';
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import { isEmpty, isEqual } from 'lodash';
import React, { ChangeEvent, FC, useContext, useState } from 'react';
import RadioButton from 'carbon-react/lib/components/radio-button';
import Box from 'carbon-react/lib/components/box';
import DateInput, { DateChangeEvent } from 'carbon-react/lib/components/date';
import { Sidebar } from '@futrli/ui-components';
import Typography from 'carbon-react/lib/components/typography';

import { VatDeferral } from '@futrli-sdk/predict';
import { FormattedMessage, getCurrencySymbolFromCode, useIntl } from '@futrli/localisation';
import { UICarbonNumberField } from '@futrli/ui-components';

import { BLACKLIST_NUMBER_VALUES, TRACKING_EVENTS } from '../../../core/constants';
import { formatDate } from '../../../core/formatters';
import { trackEvent } from '../../../core/tracker';
import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';
import baseStyles from '../../../ui/modal/modal.scss';
import styles from './modal.scss';
import { forceLocalDate } from '../../../utils/dates';

interface OwnProps {
  onClose: () => void;
  onSuccess: (deferral: VatDeferral) => Promise<void>;
  amount?: string;
  taxName: string;
  expectedPaymentDate?: Date;
  isSplitPayment?: boolean;
  hasUpdateFailed?: boolean;
}

interface ErrorState {
  [key: string]: string;
}

enum MODAL_FIELDS {
  AMOUNT = 'amount',
  EXPECTED_PAYMENT_DATE = 'expected_payment_date'
}

type Props = OwnProps;

const MAX_EXPECTED_PAYMENT_LIMIT = 2;
const MAX_AMOUNT = 9999999999;

export const SidebarDeferredVatPayment: FC<Props> = (prop: Props) => {
  const {
    onSuccess,
    onClose,
    taxName,
    amount: amountProp = '',
    expectedPaymentDate: expectedPaymentDateProp = null,
    isSplitPayment: isSplitPaymentProps = false,
    hasUpdateFailed = false
  } = prop;

  const intl = useIntl();

  const { currency } = useContext(PredictManagementContext);
  const currencySymbol = getCurrencySymbolFromCode(currency!);
  const [errors, setErrors] = useState<ErrorState>({});
  const [amount, setAmount] = useState(amountProp);
  const [isSplitPayment, setIsSplitPayment] = useState(isSplitPaymentProps);
  const [expectedPaymentDate, setExpectedPaymentDate] = useState<Date | null>(expectedPaymentDateProp);

  const MAX_DATE = addYears(Date.now(), MAX_EXPECTED_PAYMENT_LIMIT);
  const MIN_DATE = Date.now();
  const MIN_SPLIT_PAYMENT_DATE = addYears(Date.now(), -1);

  const hasError = (fieldName: keyof VatDeferral): boolean => {
    return errors[fieldName] !== undefined;
  };

  const getError = (fieldName: keyof VatDeferral): string | undefined => {
    return errors[fieldName] || undefined;
  };

  const validateAmount = (amountToValidate = amount): string | undefined => {
    if (!amountToValidate || BLACKLIST_NUMBER_VALUES.includes(amountToValidate) || parseFloat(amountToValidate) === 0) {
      return intl.formatMessage({ id: 'common.errorMsg.invalidNumber', defaultMessage: 'Must be a valid number' });
    }

    return;
  };

  const validateExpectedPaymentDate = (paymentDate = expectedPaymentDate): string | undefined => {
    if (!paymentDate) {
      return intl.formatMessage({ id: 'common.select_date', defaultMessage: 'Please select a date' });
    }

    return;
  };

  const validateForm = () => {
    const errorState: ErrorState = {};

    const amountError = validateAmount();
    const expectedPaymentDateError = validateExpectedPaymentDate();
    let isValid = true;

    if (amountError) {
      errorState[MODAL_FIELDS.AMOUNT] = amountError;
      isValid = false;
    }

    if (expectedPaymentDateError) {
      errorState[MODAL_FIELDS.EXPECTED_PAYMENT_DATE] = expectedPaymentDateError;
      isValid = false;
    }

    if (!isEqual(errors, errorState)) {
      setErrors(errorState);
    }

    return isValid;
  };

  const updateErrors = (error: string | undefined, fieldName: keyof VatDeferral) => {
    const errorState = Object.assign({}, errors);

    if (error) {
      errorState[fieldName] = error;
    } else {
      delete errorState[fieldName];
    }

    setErrors(errorState);
  };

  const handleModalAction = (): void => {
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    trackEvent(TRACKING_EVENTS.SAVED_DEFERRED_VAT);
    onSuccess({
      amount: parseFloat(amount),
      expected_payment_date: formatDate(expectedPaymentDate!),
      split_over_12_months: isSplitPayment
    });
  };

  const handleAmountChange = () => (value: string): void => {
    setAmount(value);
    const error = validateAmount(value);
    if (errors.amount !== error) {
      updateErrors(error, MODAL_FIELDS.AMOUNT);
    }
  };

  const handleExpectedPaymentDateChangeCarbon = (e: DateChangeEvent) => {
    const newDate = forceLocalDate(e.target.value.rawValue || '');
    setExpectedPaymentDate(newDate);
    const error = validateExpectedPaymentDate(newDate);
    if (errors.expected_payment_date !== error) {
      updateErrors(error, MODAL_FIELDS.EXPECTED_PAYMENT_DATE);
    }
  };

  const handleIsSplitPaymentChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    const isSplitPayment = value === 'true';
    setIsSplitPayment(isSplitPayment);
    setExpectedPaymentDate(null);
  };

  return (
    <Sidebar
      open={true}
      aria-label={intl.formatMessage({
        id: 'predict.deferred_vat_payment_modal.title',
        defaultMessage: 'Deferred {taxName} payment'
      })}
      header={
        <Typography variant="h2" fontWeight="700">
          {intl.formatMessage(
            {
              id: 'predict.deferred_vat_payment_modal.title',
              defaultMessage: 'Deferred {taxName} payment'
            },
            { taxName }
          )}
        </Typography>
      }
      onCancel={onClose}
    >
      <Form
        stickyFooter
        leftSideButtons={
          <Button onClick={onClose} buttonType="secondary" data-testid="dialog-actions-secondary">
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
        }
        saveButton={
          <Button
            disabled={!isEmpty(errors)}
            buttonType="primary"
            onClick={handleModalAction}
            data-testid="dialog-actions-primary"
          >
            <FormattedMessage id="common.confirm" defaultMessage="Confirm" />
          </Button>
        }
        onSubmit={handleModalAction}
      >
        <div data-testid="dialog-content">
          <p className="uiTypeSmallHeader" data-testid="modal-info-header">
            <FormattedMessage
              id="predict.deferred_vat_payment_modal.header"
              defaultMessage="If the balance of your {taxName} liability account includes a payment you’ve deferred, tell us about it here."
              values={{ taxName }}
            />
          </p>
          <Box mt="8px" mb="16px">
            <RadioButton
              id="payment_lump"
              checked={!isSplitPayment}
              value="false"
              name="payment_mode"
              onChange={handleIsSplitPaymentChange}
              label={
                <FormattedMessage
                  id="predict.deferred_vat_payment_modal.lump_payment_label"
                  defaultMessage="Pay deferral in one lump payment"
                />
              }
              aria-label={intl.formatMessage({
                id: 'predict.deferred_vat_payment_modal.lump_payment_label',
                defaultMessage: 'Pay deferral in one lump payment'
              })}
              size="large"
            />
          </Box>
          <Box className={baseStyles.formRow}>
            <RadioButton
              id="split_payment"
              checked={isSplitPayment}
              name="payment_mode"
              value="true"
              onChange={handleIsSplitPaymentChange}
              aria-label={intl.formatMessage({
                id: 'predict.deferred_vat_payment_modal.split_payment_label',
                defaultMessage: 'Split the payment over 12 months'
              })}
              size="large"
              label={
                <FormattedMessage
                  id="predict.deferred_vat_payment_modal.split_payment_label"
                  defaultMessage="Split the payment over 12 months"
                />
              }
            />
          </Box>
          <div className={baseStyles.formRow}>
            <UICarbonNumberField
              label={
                isSplitPayment
                  ? intl.formatMessage({
                      id: 'predict.deferred_vat_payment_modal.total_deferred_amount',
                      defaultMessage: 'Total deferred amount'
                    })
                  : intl.formatMessage({ id: 'common.amount', defaultMessage: 'Amount' })
              }
              autoComplete="off"
              className={styles.formInput}
              currencySymbol={currencySymbol}
              error={hasError(MODAL_FIELDS.AMOUNT) ? getError(MODAL_FIELDS.AMOUNT) : undefined}
              id={MODAL_FIELDS.AMOUNT}
              name={MODAL_FIELDS.AMOUNT}
              onChange={handleAmountChange()}
              clampMax={MAX_AMOUNT}
              placeholder=""
              type="currency"
              value={amount}
            />
            {isSplitPayment && (
              <div className={classnames('uiTypeCard', styles.helperText)}>
                <FormattedMessage
                  id="predict.deferred_vat_payment_modal.total_def_amt_help_text"
                  defaultMessage="Please enter the original amount that was deferred, even if you have started repayment."
                />
              </div>
            )}
          </div>
          <div className={styles.dateInput}>
            <DateInput
              width="100%"
              label={
                isSplitPayment
                  ? intl.formatMessage({
                      id: 'predict.deferred_vat_payment_modal.first_payment_date',
                      defaultMessage: 'First payment date'
                    })
                  : intl.formatMessage({
                      id: 'predict.deferred_vat_payment_modal.expected_payment_date',
                      defaultMessage: 'Expected payment date'
                    })
              }
              id={MODAL_FIELDS.EXPECTED_PAYMENT_DATE}
              name={MODAL_FIELDS.EXPECTED_PAYMENT_DATE}
              error={
                hasError(MODAL_FIELDS.EXPECTED_PAYMENT_DATE) ? getError(MODAL_FIELDS.EXPECTED_PAYMENT_DATE) : undefined
              }
              maxDate={format(MAX_DATE, 'yyyy-MM-dd')}
              minDate={isSplitPayment ? format(MIN_SPLIT_PAYMENT_DATE, 'yyyy-MM-dd') : format(MIN_DATE, 'yyyy-MM-dd')}
              onChange={handleExpectedPaymentDateChangeCarbon}
              value={
                expectedPaymentDate
                  ? intl.formatDate(expectedPaymentDate, {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })
                  : ''
              }
            />
            {isSplitPayment && (
              <div className={classnames('uiTypeCard', styles.helperText)}>
                <FormattedMessage
                  id="predict.deferred_vat_payment_modal.first_payment_date_text"
                  defaultMessage="Please tell us the first payment date, we will split the deferral into 12 monthly payments. The date should be in the past if you have already started paying back the deferral."
                />
              </div>
            )}
          </div>
          {hasUpdateFailed && (
            <div className="uiTypeLabelRed">
              <FormattedMessage id="common.something_gone_wrong" defaultMessage="Whoops… Something’s gone wrong!" />
            </div>
          )}
        </div>
      </Form>
    </Sidebar>
  );
};
